/**
 * TABsense redirect_uri must exactly match the URL registered with TABsense (and the backend token exchange).
 * Set REACT_APP_TABSENSE_REDIRECT_URI per environment, e.g.:
 * https://onehub.staging.loops.sa/oauth/tabsense/callback
 * Falls back to current origin for local development.
 */
export function getTabSenseRedirectUri() {
  const fromEnv = process.env.REACT_APP_TABSENSE_REDIRECT_URI;
  if (fromEnv && String(fromEnv).trim() !== '') {
    return String(fromEnv).trim();
  }
  return `${window.location.origin}/oauth/tabsense/callback`;
}

/** Full authorize URL override (optional). */
const DEFAULT_TABSENSE_AUTH_ORIGIN = 'https://sandbox-app.tabsense.ai';

/**
 * Sandbox / staging partner client id — used only when env is missing on non-production hosts.
 * Production must set REACT_APP_TABSENSE_CLIENT_ID (e.g. a2199c22-8a3f-4535-b9f1-b091cad2b59d).
 */
const STAGING_TABSENSE_CLIENT_ID = 'a10e2eff-e941-4b46-b68a-7c67fd611d6e';

const NON_PRODUCTION_HOSTS = [
  'onehub.staging.loops.sa',
  'onehub.demo.loops.sa',
  'localhost',
  '127.0.0.1',
];

function isNonProductionHost() {
  if (typeof window === 'undefined' || !window.location?.hostname) {
    return false;
  }
  return NON_PRODUCTION_HOSTS.includes(window.location.hostname);
}

/** True when env value is a real client id (not empty / deploy placeholder). */
function isUsableClientId(value) {
  if (value == null || String(value).trim() === '') {
    return false;
  }
  const v = String(value).trim();
  if (v.startsWith('<') && v.endsWith('>')) {
    return false;
  }
  if (/staging-client-id/i.test(v)) {
    return false;
  }
  return true;
}

/**
 * Resolve TabSense OAuth client_id for the authorize redirect.
 * - Prefer REACT_APP_TABSENSE_CLIENT_ID when set to a real value.
 * - On staging/local only, fall back to the sandbox client id.
 * - On production, require env (no hardcoded production id).
 */
export function getTabSenseClientId() {
  const fromEnv = process.env.REACT_APP_TABSENSE_CLIENT_ID;
  if (isUsableClientId(fromEnv)) {
    return String(fromEnv).trim();
  }
  if (isNonProductionHost()) {
    return STAGING_TABSENSE_CLIENT_ID;
  }
  return null;
}

/**
 * OAuth step 2 — GET authorize endpoint per TABsense docs:
 * {auth_base}/3rdparty/v1/oauth/authorize
 * Sandbox default: https://sandbox-app.tabsense.ai/3rdparty/v1/oauth/authorize
 *
 * Override with REACT_APP_TABSENSE_AUTHORIZE_URL (full URL) or REACT_APP_TABSENSE_AUTH_BASE_URL (origin only).
 */
export function getTabSenseAuthorizeUrl() {
  const full = process.env.REACT_APP_TABSENSE_AUTHORIZE_URL;
  if (full && String(full).trim() !== '') {
    return String(full).trim().replace(/\/$/, '');
  }
  const origin =
    (process.env.REACT_APP_TABSENSE_AUTH_BASE_URL &&
      String(process.env.REACT_APP_TABSENSE_AUTH_BASE_URL).trim()) ||
    DEFAULT_TABSENSE_AUTH_ORIGIN;
  return `${String(origin).replace(/\/$/, '')}/3rdparty/v1/oauth/authorize`;
}
